import { useState } from "react";
import { toast } from "sonner";
import { Lock, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable } from "@/components/app/DataTable";
import { AccessImportButton } from "@/components/admin/AccessImport";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

function RoleDialog({ value, roles, onClose, onSaved }) {
  const isNew = !value.key;
  const [f, setF] = useState({ name: value.name || "", purpose: value.purpose || "", copy_from: "viewer" });
  const save = async () => {
    try {
      isNew ? await api.post("/roles", { name: f.name, purpose: f.purpose, copy_from: f.copy_from === "none" ? null : f.copy_from })
        : await api.put(`/roles/${value.key}`, { name: f.name, purpose: f.purpose });
      toast.success("Role saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="role-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New role" : `Edit ${value.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Role name *</Label><Input value={f.name} disabled={value.builtin} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Service Head, Design Engineer" data-testid="role-name-input" /></div>
          <div><Label>Purpose</Label><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="role-purpose-input" /></div>
          {isNew && <div><Label>Start with the permissions of</Label>
            <Select value={f.copy_from} onValueChange={(v) => setF({ ...f, copy_from: v })}><SelectTrigger data-testid="role-copy-from"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Nothing (blank)</SelectItem>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent></Select></div>}
        </div>
        <DialogFooter><Button onClick={save} data-testid="role-save-btn">Save role</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RolesPanel() {
  const { data, reload } = useFetch("/roles");
  const [edit, setEdit] = useState(null);
  const toggle = async (r) => { try { await api.put(`/roles/${r.key}`, { active: !r.active }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const cols = [
    { key: "name", label: "Role", render: (r) => <span className="font-medium">{r.name}{r.builtin && <span className="ml-1 text-[10px] text-slate-400">built-in</span>}</span> },
    { key: "purpose", label: "Purpose", wrap: true }, { key: "permissions", label: "Permissions", right: true, exp: (r) => r.permissions.length, render: (r) => r.permissions.length },
    { key: "users", label: "Active users", right: true },
    { key: "active", label: "Active", exp: (r) => (r.active ? "Yes" : "No"), render: (r) => r.builtin ? <Lock className="h-3.5 w-3.5 text-slate-400" /> : <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`role-active-${r.key}`} /> },
    { key: "edit", label: "", exp: false, render: (r) => <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`role-edit-${r.key}`}><Pencil className="h-4 w-4" /></Button> },
  ];
  return (
    <div data-testid="admin-roles">
      <p className="text-xs text-slate-500 mb-3">A person can hold several roles; their permissions add up. Built-in roles can't be renamed or switched off. Admin always has every permission. A switched-off role gives its holders nothing. Set what each role can do under Permissions.</p>
      <DataTable testid="roles-table" columns={cols} rows={data} rowId={(r) => r.key} exportName="Roles"
        toolbar={<><AccessImportButton onDone={reload} /><Button size="sm" onClick={() => setEdit({})} data-testid="add-role-btn"><Plus className="h-4 w-4 mr-1" />New role</Button></>} />
      {edit && <RoleDialog value={edit} roles={data || []} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
